import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, User } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthLayout } from '../../../components/layouts';
import {
    CountrySelectInput,
    PhoneNumberInput,
    StateSelectInput,
    Stepper,
    TimezoneSelect,
} from '../../../components/ui';
import AuthInput from '../../../components/ui/AuthInput';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import AuthService from '../../../services/auth.service';

export type AuthProps = {
    props: {
        setFormData: (data: any) => void;
        loading?: boolean;
        formData: {
            firstName: string;
            lastName: string;
            email: string;
            country: string;
            state: string;
            phone: string;
            password: string;
            timezone: string;
            survey?: string;
            preferredClass?: string;
            smsOptIn?: boolean;
        };
    };
    setActiveTab: () => void;
};

const selectStyle =
    'w-full rounded-[12px] border border-[#E8E0EC] bg-white px-4 py-3 text-[14px] text-[#334155] outline-none focus:border-ryd-primary';

/** Step 1 — Personal info (original flow) with new design. */
export default function PersonalInfo({ props, setActiveTab }: AuthProps) {
    const authService = new AuthService();
    const navigate = useNavigate();
    const { setFormData, formData } = props;
    const [country, setCountry] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);

    const isEmailAlreadyUsedMessage = (message: string = '') =>
        message.toLowerCase().includes('email already used') ||
        message.toLowerCase().includes('already exists');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agreed) {
            toast.error('Please agree to the Terms and Privacy Policy');
            return;
        }
        if (!formData.timezone) {
            toast.error('Timezone is required!');
            return;
        }
        if (!formData.state?.trim()) {
            toast.error('State is required!');
            return;
        }
        if (!formData.phone?.trim()) {
            toast.error('Phone number is required!');
            return;
        }
        if (!formData.survey?.trim()) {
            toast.error('Please tell us how you heard about us');
            return;
        }
        if (!formData.preferredClass?.trim()) {
            toast.error('Please choose which class you would like to enroll your child in');
            return;
        }

        setLoading(true);
        try {
            const response = await authService.verifyEmail({ email: formData.email });
            setLoading(false);
            if (!response.status) {
                if (isEmailAlreadyUsedMessage(response?.message)) {
                    toast.error('This email already has an account. Please sign in or reset password.');
                    navigate('/parent/sign-in');
                    return;
                }
                toast.error(response?.message);
                return;
            }
            localStorage.setItem('email-confirmation', JSON.stringify(response));
            setActiveTab();
        } catch (err: any) {
            setLoading(false);
            toast.error(err?.message);
        }
    };

    return (
        <AuthLayout>
            <Stepper currentTab={1} />

            <div className="mb-5 text-center">
                <h1 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[26px]">
                    Create your account
                </h1>
                <p className="mt-1.5 px-1 text-[13px] text-[#8b9aab] sm:text-[14px]">
                    Let&apos;s start with your personal information.
                </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                    <AuthInput
                        label="First Name"
                        placeholder="John"
                        required
                        leftIcon={<User size={18} />}
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    />
                    <AuthInput
                        label="Last Name"
                        placeholder="Doe"
                        required
                        leftIcon={<User size={18} />}
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    />
                </div>

                <AuthInput
                    label="Email Address"
                    type="email"
                    placeholder="example@email.com"
                    required
                    leftIcon={<Mail size={18} />}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">
                            Country
                        </label>
                        <CountrySelectInput
                            handleCountryChange={(data: any) => {
                                setCountry(data);
                                setFormData({ ...formData, country: data.name, state: '' });
                            }}
                            className={selectStyle}
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">
                            Province / State
                        </label>
                        <StateSelectInput
                            country={country}
                            handleStateChange={(data: any) => setFormData({ ...formData, state: data.name })}
                            className={selectStyle}
                        />
                    </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">
                            Phone
                        </label>
                        <PhoneNumberInput
                            country={country}
                            handlePhoneInputChange={(data: string) => setFormData({ ...formData, phone: data })}
                            className={selectStyle}
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">
                            Study Timezone
                        </label>
                        <TimezoneSelect
                            country={country}
                            handleTimezoneChange={(data: any) =>
                                setFormData({ ...formData, timezone: data.zoneName })
                            }
                            className={selectStyle}
                            placeholder="Select timezone"
                        />
                    </div>
                </div>

                <div>
                    <label
                        htmlFor="survey"
                        className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary"
                    >
                        How did you hear about us?
                    </label>
                    <select
                        id="survey"
                        name="survey"
                        required
                        value={formData.survey || ''}
                        onChange={(e) => setFormData({ ...formData, survey: e.target.value })}
                        className={selectStyle}
                    >
                        <option value="">Select an option</option>
                        <option value="Google">Google</option>
                        <option value="Group Chats">Group Chats</option>
                        <option value="Friends">Friends</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Others">Others</option>
                    </select>
                </div>

                <div>
                    <label
                        htmlFor="preferredClass"
                        className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary"
                    >
                        Which class would you like to enroll your child in?
                    </label>
                    <select
                        id="preferredClass"
                        name="preferredClass"
                        required
                        value={formData.preferredClass || ''}
                        onChange={(e) => setFormData({ ...formData, preferredClass: e.target.value })}
                        className={selectStyle}
                    >
                        <option value="">Select an option</option>
                        <option value="coding">Coding</option>
                        <option value="mathematics">Mathematics</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <label className="flex items-start gap-2.5 text-[13px] text-[#476788]">
                    <input
                        type="checkbox"
                        checked={formData.smsOptIn !== false}
                        onChange={(e) => setFormData({ ...formData, smsOptIn: e.target.checked })}
                        className="mt-0.5 accent-ryd-primary"
                    />
                    <span>
                        Opt in to receive SMS updates about classes, reminders, and important account notices.
                    </span>
                </label>

                <label className="flex items-start gap-2.5 text-[13px] text-[#476788]">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-0.5 accent-ryd-primary"
                        required
                    />
                    <span>
                        I agree to the{' '}
                        <a
                            href="https://rydlearning.com/terms/"
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-ryd-primary hover:underline"
                        >
                            Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a
                            href="https://rydlearning.com/terms/#privacy-policy"
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-ryd-primary hover:underline"
                        >
                            Privacy Policy
                        </a>
                    </span>
                </label>

                <AuthPrimaryButton loading={loading}>Continue</AuthPrimaryButton>

                <p className="pt-1 text-center text-[14px] text-[#8b9aab]">
                    Already have an account?{' '}
                    <Link to="/parent/sign-in" className="font-semibold text-ryd-primary hover:underline">
                        Sign In
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
}
